import { psAxios } from '@patsnap/common-ps-http';
import { get, isObject } from 'lodash';
import type { AxiosRequestConfig } from 'axios';
import type {
    I18nInstance,
    ExtendedAxiosRequestConfig,
    ExtendedAxiosResponse,
    ExtendedAxiosError,
    HttpStatusCodeMap,
} from './types';

/**
 * 对应不上的请求错误码通过 error message 逆推
 */
const httpStatusCodes: HttpStatusCodeMap = {
    'Network Error': 440,
};

/**
 * 获取错误信息的国际化文本
 * @param errorCode - 错误码
 * @param errorParams - 错误参数（用于插值）
 * @param i18n - i18n 实例
 * @param prefix - 翻译键前缀
 * @returns 国际化后的错误信息
 */
const getErrorMessage = (
    errorCode: number | string,
    errorParams: Record<string, any> = {},
    i18n: I18nInstance | undefined,
    prefix: string = 'commonStatusCode',
): string => {
    if (!i18n || !i18n.te) return '';
    const i18nKey = `${prefix}.${errorCode}`;
    if (i18n.te(i18nKey)) {
        return i18n.t(i18nKey, errorParams);
    }
    return i18n.t('commonStatusCode.400');
};

/**
 * 处理响应数据，添加错误码和错误信息
 * @param response - Axios 响应对象
 * @param i18n - i18n 实例
 * @param prefix - 翻译键前缀
 * @returns 是否启用了 transformCode
 */
const processResponse = (
    response: ExtendedAxiosResponse,
    i18n: I18nInstance | undefined,
    prefix: string = 'commonStatusCode',
): boolean => {
    const { transformCode } = response.config;
    const errorCode = get(response, 'data.error_code', 400) as number;
    const errorParams = get(response, 'data.error_params') as Record<string, any> || {};

    if (transformCode && errorCode !== 0) {
        const errorMsg = getErrorMessage(errorCode, errorParams, i18n, prefix);
        response.data = isObject(response.data)
            ? {
                ...response.data,
                _error_code: errorCode,
                _error_msg: errorMsg,
            }
            : {
                _error_code: errorCode,
                _error_msg: errorMsg,
            };
    }
    return transformCode ?? false;
};

/**
 * 处理错误响应
 * @param error - Axios 错误对象
 * @param i18n - i18n 实例
 * @param prefix - 翻译键前缀
 * @returns 是否启用了 transformCode
 */
const processErrorResponse = (
    error: ExtendedAxiosError,
    i18n: I18nInstance | undefined,
    prefix: string = 'commonStatusCode',
): boolean => {
    if(!error?.toJSON) return false;

    // 如果错误没有 response 则构造一个
    const errorJson = error.toJSON() as any;
    const { code, message, config } = errorJson;
    const { transformCode } = config as ExtendedAxiosRequestConfig;

    if (!transformCode) return false;

    if (!error.response) {
        error.response = {
            config: config as AxiosRequestConfig,
            data: {
                error_code: code || httpStatusCodes[message],
            },
            status: 0,
            statusText: '',
            headers: {},
        } as ExtendedAxiosResponse;
    } else if (!get(error, 'response.data.error_code')) {
        // 补上状态码错误
        const statusCode = code || httpStatusCodes[message] || `sc.${error.response.status}`;
        error.response.data.error_code = typeof statusCode === 'string' ? parseInt(statusCode) : statusCode;
    }

    return processResponse(error.response, i18n, prefix);
};

/**
 * 创建自定义 Axios 错误
 * @param message - 错误信息
 * @param config - 请求配置
 * @param code - 错误码
 * @param request - 请求对象
 * @param response - 响应对象
 * @returns Axios 错误对象
 */
const createAxiosError = (
    message: string,
    config: ExtendedAxiosRequestConfig,
    code: string | null,
    request: any,
    response: ExtendedAxiosResponse,
): ExtendedAxiosError => {
    const error = new Error(message) as ExtendedAxiosError;
    error.config = config;
    error.code = code || undefined;
    error.request = request;
    error.response = response;
    error.isAxiosError = true;
    error.toJSON = function() {
        return {
            message: this.message,
            name: this.name,
            config: this.config,
            code: this.code,
        };
    };
    return error;
};

/**
 * 初始化错误码转换拦截器
 * @param i18n - i18n 实例
 * @param prefix - 翻译键前缀，默认 'commonStatusCode'
 */
export const initTransformCodeInterceptor = (
    i18n?: I18nInstance,
    prefix: string = 'commonStatusCode',
): void => {
    // 在 then 中的状态 200 的业务错误
    // error_code 必然存在
    const transformCodeThenInterceptor = (response: ExtendedAxiosResponse): Promise<ExtendedAxiosResponse> | ExtendedAxiosResponse => {
        if (!processResponse(response, i18n, prefix)) {
            return response;
        }

        const { _error_code, _error_msg } = response.data;

        // 如果有 status 字段，直接返回（某些业务场景）
        if (get(response, 'data.status')) {
            return response;
        }

        const newAxiosError = createAxiosError(
            _error_msg || `Request failed with status code ${_error_code}`,
            response.config,
            null,
            response.request,
            response,
        );

        newAxiosError._error_code = _error_code;
        newAxiosError._error_msg = _error_msg;
        return Promise.reject(newAxiosError);
    };

    // 在 catch 中的状态 200 的业务错误和请求状态码非 200 的错误
    // error_code 未必存在，存在的时候应该不为 0
    const transformCodeCatchInterceptor = (error: ExtendedAxiosError): Promise<ExtendedAxiosError> => {
        if (!processErrorResponse(error, i18n, prefix)) {
            return Promise.reject(error);
        }

        const { _error_code, _error_msg } = error.response!.data;
        error._error_code = _error_code;
        error._error_msg = _error_msg;
        return Promise.reject(error);
    };

    psAxios.interceptors.response.use(transformCodeThenInterceptor, transformCodeCatchInterceptor);
};
